"use client";

import { ArrowLeft, Check, Globe, Lock } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Card, CardContent } from "@/shadcn-components/ui/card";
import { Badge } from "@/shadcn-components/ui/badge";
import { useSettingsStore, LANGUAGES } from "@/lib/settings";

interface LanguagesContentProps {
  onBack: () => void;
}

export function LanguagesContent({ onBack }: LanguagesContentProps) {
  const { settings } = useSettingsStore();
  const selectedLanguage = settings.language;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-6 border-b border-border">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
              <Globe className="h-5 w-5 text-blue-600" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Languages</h2>
              <p className="text-sm text-muted-foreground">
                Choose your preferred language
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Language List */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        <div className="space-y-6">
          {/* Supported Languages Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-muted-foreground text-sm uppercase font-medium tracking-wide">
                SUPPORTED LANGUAGES
              </h3>
            </div>
            <Card>
              <CardContent className="p-0">
                {LANGUAGES.map((lang, index) => (
                  <div key={lang.id}>
                    <div
                      className={`flex items-center w-full p-4 rounded-none ${
                        index === 0 ? "rounded-t-lg" : ""
                      } ${
                        index === LANGUAGES.length - 1 ? "rounded-b-lg" : ""
                      } ${
                        lang.enabled
                          ? ""
                          : "opacity-45 cursor-not-allowed select-none"
                      }`}
                      aria-disabled={!lang.enabled}
                    >
                      <div className="text-2xl mr-3">{lang.flag}</div>
                      <div className="flex-1 text-left">
                        <div className="font-medium">{lang.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {lang.enabled ? "Default" : lang.nativeName}
                        </div>
                      </div>
                      {lang.enabled ? (
                        <>
                          <Badge variant="default" className="ml-2">
                            Active
                          </Badge>
                          {selectedLanguage === lang.id && (
                            <Check className="h-4 w-4 text-blue-400 ml-2" />
                          )}
                        </>
                      ) : (
                        <Badge
                          variant="outline"
                          className="ml-2 text-muted-foreground"
                        >
                          Coming soon
                        </Badge>
                      )}
                    </div>
                    {index < LANGUAGES.length - 1 && (
                      <div className="h-px bg-border mx-4" />
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Lock Notice Card */}
          <Card className="border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-950/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center flex-shrink-0">
                  <Lock className="h-4 w-4 text-amber-600" />
                </div>
                <div className="text-sm">
                  <div className="font-medium mb-1">Language switching locked</div>
                  <div className="text-muted-foreground">
                    Language switching is locked while translations are in
                    progress. English is used everywhere for now.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
